/**
 * Iter C.2.1.d — Commercial Commitments editor (R-B*).
 *
 * A focused authoring control for the `commitments[]` field on a line
 * item. Per the locked Layer 2 contract (/app/memory/C2.1_AUDIT.md §1.5):
 *
 *   • A Package owns 1..N typed commercial commitments.
 *   • Each commitment carries: label (free-text, NO taxonomy enforcement
 *     at this stage), amount, cadence (billing frequency), GST %, HSN
 *     code, activation rule, optional description.
 *   • Empty list ⇒ legacy (amount, amc_amount) line shape preserved.
 *   • Non-empty list ⇒ line.amount auto-syncs server-side to the sum.
 *
 * This component is presentation-only — it does not call the API. It
 * raises `onChange(commitments)` with the new array; the parent dialog
 * handles persistence at submit time.
 */
import React from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const CADENCE_SUGGESTIONS = [
    "One-time", "Monthly", "Quarterly", "Annual", "Bi-annual",
];

export function CommitmentsEditor({
    commitments,
    onChange,
    defaultGstPercent = 18,
    defaultHsn = "997331",
}) {
    const list = Array.isArray(commitments) ? commitments : [];
    const total = list.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

    const add = () => onChange([
        ...list,
        {
            id: null,
            label: "",
            amount: 0,
            cadence: "Annual",
            gst_percent: defaultGstPercent,
            hsn_sac_code: defaultHsn,
            activation_rule: null,
            description: null,
        },
    ]);

    const update = (idx, patch) => onChange(
        list.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    );

    const remove = (idx) => onChange(list.filter((_, i) => i !== idx));

    return (
        <div
            className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2"
            data-testid="commitments-editor"
        >
            <div className="flex items-center justify-between">
                <div>
                    <Label className="text-xs font-semibold">
                        Commercial commitments <span className="font-normal text-slate-400">(optional · R-B*)</span>
                    </Label>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                        Use this when one Package contains multiple commercial
                        commitments — e.g. License + AMC + Hosting.
                        {list.length > 0 ? (
                            <> Sum: <span className="font-mono font-semibold text-slate-700">₹{total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></>
                        ) : null}
                    </p>
                </div>
                <Button
                    type="button" variant="outline" size="sm"
                    onClick={add}
                    className="gap-1.5"
                    data-testid="commitments-add-btn"
                >
                    <Plus size={14} weight="bold" /> Add commitment
                </Button>
            </div>

            {list.length === 0 && (
                <p className="text-[11px] text-slate-400 italic px-1 py-2" data-testid="commitments-empty">
                    No commitments. Legacy Software Fee + AMC Fee shape will be used.
                </p>
            )}

            {list.length > 0 && (
                <div className="space-y-2" data-testid="commitments-list">
                    {list.map((c, idx) => (
                        <div
                            key={c.id || `idx-${idx}`}
                            className="grid grid-cols-12 gap-2 items-end bg-white border border-slate-200 rounded-md p-2.5"
                            data-testid={`commitment-row-${idx}`}
                        >
                            <div className="col-span-3">
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Label</Label>
                                <Input
                                    value={c.label || ""}
                                    onChange={(e) => update(idx, { label: e.target.value.slice(0, 160) })}
                                    placeholder="License / AMC / Hosting / Data Maint."
                                    className="mt-1 h-8 text-sm"
                                    required
                                    data-testid={`commitment-label-${idx}`}
                                />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Amount (₹)</Label>
                                <Input
                                    type="number" min="0" step="0.01"
                                    value={c.amount ?? 0}
                                    onChange={(e) => update(idx, { amount: e.target.value })}
                                    className="mt-1 h-8 text-sm font-mono"
                                    required
                                    data-testid={`commitment-amount-${idx}`}
                                />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Cadence</Label>
                                <Input
                                    list={`cadence-options-${idx}`}
                                    value={c.cadence || ""}
                                    onChange={(e) => update(idx, { cadence: e.target.value })}
                                    placeholder="Annual"
                                    className="mt-1 h-8 text-sm"
                                    data-testid={`commitment-cadence-${idx}`}
                                />
                                <datalist id={`cadence-options-${idx}`}>
                                    {CADENCE_SUGGESTIONS.map((opt) => (
                                        <option key={opt} value={opt} />
                                    ))}
                                </datalist>
                            </div>
                            <div className="col-span-1">
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">GST %</Label>
                                <Input
                                    type="number" min="0" max="28" step="1"
                                    value={c.gst_percent ?? defaultGstPercent}
                                    onChange={(e) => update(idx, { gst_percent: e.target.value })}
                                    className="mt-1 h-8 text-sm font-mono"
                                    data-testid={`commitment-gst-${idx}`}
                                />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">HSN/SAC</Label>
                                <Input
                                    value={c.hsn_sac_code || ""}
                                    onChange={(e) => update(idx, { hsn_sac_code: e.target.value.slice(0, 20) })}
                                    placeholder="997331"
                                    className="mt-1 h-8 text-sm font-mono"
                                    data-testid={`commitment-hsn-${idx}`}
                                />
                            </div>
                            <div className="col-span-1">
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Activation</Label>
                                <Input
                                    value={c.activation_rule || ""}
                                    onChange={(e) => update(idx, { activation_rule: e.target.value.slice(0, 80) || null })}
                                    placeholder="optional"
                                    className="mt-1 h-8 text-sm"
                                    data-testid={`commitment-activation-${idx}`}
                                />
                            </div>
                            <div className="col-span-1 flex justify-end">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => remove(idx)}
                                    className="h-8 w-8 p-0 text-rose-500 hover:bg-rose-50"
                                    data-testid={`commitment-remove-${idx}`}
                                    title="Remove commitment"
                                >
                                    <Trash size={14} weight="bold" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default CommitmentsEditor;
